<template>
	<transition name="curtain-anim" :duration="{enter: 300, leave: 500}">
		<div class="curtain" v-if="modelValue" @click.self="close()">
			<div
				class="curtain__body"
				:class="classes"
				:style="style"
				ref="curtainRef"
				@touchstart="touchstart"
				@touchmove="touchmove"
				@touchend="end"
				@mousedown="touchstart"
				@mousemove="touchmove"
				@mouseup="end"
			>
				<button class="curtain__close" @click="close()" v-if="closeBtn">
					<SvgIco name="close"/>
				</button>
				<slot/>
			</div>
		</div>
	</transition>
</template>

<script setup lang="ts">
import {computed, ref, watch} from "vue";
import SvgIco from '@shared/components/base/SvgIco.vue';

interface Style {
	transitionDuration?: string;
	transform?: string;
}

const props = withDefaults(defineProps<{
	modelValue: boolean,
  position: string,
  closeBtn?: boolean
}>(),
  {position: 'top', closeBtn: false}
)

const emit = defineEmits<{
	'update:modelValue': [value: boolean]
}>()

const curtainRef = ref<HTMLElement>();

// Reactive state for dragging functionality
const dragging = ref(false);
const touchStart = ref({x: 0, y: 0});
const touchPos = ref(0);
const touchDist = ref(0);
const lengthToClose = ref(100);

const style = ref<Style>({});

const classes = computed(() => 'curtain_pos_' + props.position);

// Watch for modelValue changes to handle body scroll lock
watch(() => props.modelValue, (newValue) => {
	if (newValue) {
		document.body.classList.add('hidden');
		document.addEventListener('touchmove', lockScroll, { passive: false });
	} else {
		document.body.classList.remove('hidden');
		document.removeEventListener('touchmove', lockScroll);
	}
});

const touchstart = (e: MouseEvent | TouchEvent) => {
	const el = curtainRef.value;
	if (!el) return;
	
	const touch = (e as TouchEvent).changedTouches ? (e as TouchEvent).changedTouches[0] : e as MouseEvent;

	touchStart.value = {x: touch.pageX, y: touch.pageY};
	touchPos.value = isNaN(parseInt(el.style.top)) ? parseInt(el.style.top) : 0;
	dragging.value = true;
	style.value.transitionDuration = '0ms';
	if (el.clientHeight < 150) lengthToClose.value = 50;
};

const touchmove = (e: MouseEvent | TouchEvent) => {
	if (dragging.value) {
		const touch = (e as TouchEvent).changedTouches ? (e as TouchEvent).changedTouches[0] : e as MouseEvent;

		if (props.position === 'left' && (touch.pageY >= touchStart.value.y + 30 || touch.pageY + 30 < touchStart.value.y)) {
			resetPos();
		} else {
			const pos = props.position === 'left' ? touch.pageX : touch.pageY;
			const direction = props.position === 'left' ? 'x' : 'y';

			touchDist.value = pos - touchStart.value[direction] + touchPos.value;

			const topCondition = props.position === 'top' && touchDist.value <= lengthToClose.value;
			const bottomCondition = props.position === 'bottom' && touchDist.value >= -lengthToClose.value;
			const leftCondition = props.position === 'left' && touchDist.value <= lengthToClose.value;

			if (topCondition || bottomCondition) {
				style.value.transform = 'translateY(' + touchDist.value + 'px)';
			} else if (leftCondition) {
				style.value.transform = 'translateX(' + touchDist.value + 'px)';
			}
		}
	}
};

const end = () => {
	const topLeftClose = (props.position === 'top' || props.position === 'left') && touchDist.value < 0 && Math.abs(touchDist.value) >= lengthToClose.value;
	const bottomClose = props.position === 'bottom' && touchDist.value > 0 && touchDist.value >= lengthToClose.value;

	if (topLeftClose || bottomClose) {
		close();
	} else {
		resetPos();
	}
	dragging.value = false;
};

const resetPos = () => {
	style.value.transitionDuration = '200ms';
	style.value.transform = 'translateX(0)';
};

const close = () => {
	style.value.transitionDuration = '500ms';

	if (props.position === 'top') {
		style.value.transform = 'translateY(-300%)';
	} else if (props.position === 'bottom') {
		style.value.transform = 'translateY(300%)';
	} else if (props.position === 'left') {
		style.value.transform = 'translateX(-300%)';
	}

	setTimeout(() => {
		emit('update:modelValue', false);
	}, 100);
	setTimeout(() => {
		delete style.value.transitionDuration;
		delete style.value.transform;
		touchStart.value = {x: 0, y: 0};
		touchPos.value = 0;
		touchDist.value = 0;
	}, 520);
};

const lockScroll = (e: Event) => {
	if (!(e.target as Element)?.closest('.curtain__body')) e.preventDefault();
};
</script>

<style>
.curtain {overflow:hidden;position:fixed;z-index:9;top:0;right:0;bottom:0;left:0;background:var(--dark-trans2);-webkit-backdrop-filter:blur(10px);backdrop-filter:blur(10px);}
.curtain__body {position:absolute;right:0;left:0;background:#fff;}
.curtain__body::before {content:'';position:absolute;left:50%;margin-left:-40px;width:80px;height:4px;background:var(--light3);border-radius:4px;}
.curtain__body::after {content:'';position:absolute;right:0;left:0;height:300%;background:#fff;}

.curtain_pos_top {top:0;padding:15px 15px 30px;border-radius:0 0 var(--radius-l) var(--radius-l);}
.curtain_pos_top::before {bottom:12px;}
.curtain_pos_top::after {bottom:100%;}

.curtain_pos_bottom {bottom:0;padding:30px 15px 15px;border-radius:var(--radius-l) var(--radius-l) 0 0;}
.curtain_pos_bottom::before {top:12px;}
.curtain_pos_bottom::after {top:100%;}

.curtain_pos_left {left:0;}
.curtain_pos_left::before {display:none;}
.curtain_pos_left:after {top:0;right:auto;bottom:0;left:-100%;width:100%;background:#fff;}

.curtain__close {position:absolute;z-index:2;top:12px;right:12px;}
.curtain__close svg {width:16px;height:16px;transition:fill .2s;fill:var(--dark1);}
.curtain__close:hover svg {fill:#777791;}

/* Transition */
.curtain-anim-enter-active,
.curtain-anim-leave-active {transition:opacity .3s;}
.curtain-anim-enter,
.curtain-anim-leave-to {opacity:0;}

.curtain-anim-enter-active .curtain__body,
.curtain-anim-leave-active .curtain__body {transition:transform .3s;}
.curtain-anim-enter .curtain_pos_top.curtain__body,
.curtain-anim-leave-to .curtain_pos_top.curtain__body {transform:translateY(-100%) !important;}
.curtain-anim-enter .curtain_pos_bottom.curtain__body,
.curtain-anim-leave-to .curtain_pos_bottom.curtain__body {transform:translateY(100%) !important;}
.curtain-anim-enter .curtain_pos_left.curtain__body,
.curtain-anim-leave-to .curtain_pos_left.curtain__body {transform:translateX(-100%) !important;}
</style>

<!-- Example
<Curtain v-model="state"></Curtain>
-->
